/* eslint-disable no-nested-ternary */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useAuthHttp } from '../../hooks';
import { useAuth } from '../../context';
import {
  ACCEPT_WAREHOUSE_MATCH,
  ADD_WAREHOUSE_MATCH,
  BUSINESS_PROFILE_GET,
  GET_MATCH_SIGNING_DETAILS,
  MATCH_LIST,
  PROPOSE_WAREHOUSE_MATCH_DETAILS,
  SUPPORTED_CURRENCY_GROUPS,
  WAREHOUSE_SEARCH_GET,
} from '../../constants';
import { UserRoleService } from '../../services';
import {
  businessProfileAtom,
  confirmationMessageAtom,
  matchAtom,
} from '../../store/jotai';
import { FormPage } from '../../tailwindComponents/FormPage';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Datepicker } from '../../tailwindComponents/Datepicker';
import { Textarea } from '../../tailwindComponents/Textarea';
import { Checkbox } from '../../tailwindComponents/Checkbox';
import { Button } from '../../tailwindComponents/Button';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { CommentThread } from '../../components/CommentThread';

// Types from WarehouseDetails
type ServiceRate = {
  originalValue: number;
  shipperProposedValue: number;
  lspProposedValue: number;
  acceptedValue: number;
};

type Warehouse = {
  warehouseId: string;
  name: string;
  address: string;
  city: string;
  currency: string;
  storageCapacityUnitOfMeasure: string;
  palletStoragePricePerWeek: number;
  squareFeetStoragePricePerWeek: number;
  squareMetreStoragePricePerWeek: number;
  palletReceiptAndPutAwayPrice: number;
  palletPickingAndDespatchPrice: number;
  palletLayerPickingAndDespatchPrice: number;
  casePickingAndDespatchPrice: number;
  subsequentCasePickingAndDespatchPrice: number;
  itemPickingAndDespatchPrice: number;
  subsequentItemPickingAndDespatchPrice: number;
  containerHandlingPrice: number;
  demurragePricePerDay: number;
  palletReturnsPrice: number;
  itemReturnsPrice: number;
  caseReturnsPrice: number;
  auxiliaryServicePricePerManHour: number;
  userBusinessName: string;
  isPartialProfile: boolean;
};

type Match = {
  id: string;
  warehouseId: string;
  shipperId: string;
  desiredStartDate: string;
  averageStorageQuantityPerWeek: number;
  estimatedDurationInMonths: number;
  averageNumberOfInboundShipmentsPerWeek: number;
  averageNumberOfOutboundShipmentsPerWeek: number;
  shipperIndustry: string;
  requestedServices: string[];
  unavailableServices: string[];
  matchStatus: string;
  billingCycleFrequency: string;
  paymentTermsInDays: number;
  originalRates: {
    storagePricePerWeek: number;
    palletReceiptAndPutAwayPrice: number;
    palletPickingAndDespatchPrice: number;
    palletLayerPickingAndDespatchPrice: number;
    casePickingAndDespatchPrice: number;
    subsequentCasePickingAndDespatchPrice: number;
    itemPickingAndDespatchPrice: number;
    subsequentItemPickingAndDespatchPrice: number;
    containerHandlingPrice: number;
    demurragePricePerDay: number;
    palletReturnsPrice: number;
    itemReturnsPrice: number;
    caseReturnsPrice: number;
    auxiliaryServicePricePerManHour: number;
  };
  serviceRates: {
    storagePricePerWeek: ServiceRate;
    palletReceiptAndPutAwayPrice: ServiceRate;
    palletPickingAndDespatchPrice: ServiceRate;
    palletLayerPickingAndDespatchPrice: ServiceRate;
    casePickingAndDespatchPrice: ServiceRate;
    subsequentCasePickingAndDespatchPrice: ServiceRate;
    itemPickingAndDespatchPrice: ServiceRate;
    subsequentItemPickingAndDespatchPrice: ServiceRate;
    containerHandlingPrice: ServiceRate;
    demurragePricePerDay: ServiceRate;
    palletReturnsPrice: ServiceRate;
    itemReturnsPrice: ServiceRate;
    caseReturnsPrice: ServiceRate;
    auxiliaryServicePricePerManHour: ServiceRate;
  };
  volumeDiscounts: {
    id: string;
    acceptedValue: number;
    lspProposedDiscountPercentage: number;
    minTotalMonthlyTurnover: number;
    maxTotalMonthlyTurnover: number;
    shipperProposedDiscountPercentage: number;
  }[];
  isActionRequiredByLsp: boolean;
  isActionRequiredByShipper: boolean;
  comments: {
    commentText: string;
    createdAt: string;
    createdByUserId: string;
    createdByUserName: string;
  }[];
  userBusinessName: string;
  legalDocuments: {
    documentId: string;
    createdAt: string;
    updatedAt: string;
    slug: string;
    documentName: string;
    name: string;
    completedAt: string;
    status: string;
    url: string;
  }[];
  floxTransactionFees: {
    storageFeePercentage: number;
    handlingFeePercentage: number;
    haulageFeePercentage: number;
    distributionFeePercentage: number;
    otherServicesPercentage: number;
    financialTransactionFeePercentage: number;
    discountPercentage: number;
  };
  matchParties: {
    userId: string;
    userBusinessId: string;
    emailAddress: string;
    isNegotiating: boolean;
    hasAccepted: boolean;
    isSigningDocument: boolean;
    name: string;
    roles: string[];
    companyNumber: string;
    companyName: string;
    companyAddress: string;
  }[];
};

export type ServiceRatesFormValues = {
  storagePricePerWeek: number | null;
  palletReceiptAndPutAwayPrice: number | null;
  palletPickingAndDespatchPrice: number | null;
  palletLayerPickingAndDespatchPrice: number | null;
  casePickingAndDespatchPrice: number | null;
  subsequentCasePickingAndDespatchPrice: number | null;
  itemPickingAndDespatchPrice: number | null;
  subsequentItemPickingAndDespatchPrice: number | null;
  containerHandlingPrice: number | null;
  demurragePricePerDay: number | null;
  palletReturnsPrice: number | null;
  itemReturnsPrice: number | null;
  caseReturnsPrice: number | null;
  auxiliaryServicePricePerManHour: number | null;
  requestedServices: string[];
  volumeBasedDiscount: boolean;
  monthlyTurnoverOne: number;
  monthlyDiscountOne: number;
  monthlyTurnoverTwo: number;
  monthlyDiscountTwo: number;
  monthlyTurnoverThree: number;
  monthlyDiscountThree: number;
  billingCycleFrequency: { label: string; value: string };
  paymentTermsInDays: { label: string; value: number };
  desiredStartDate: string;
  estimatedDurationInMonths: number;
  averageStorageQuantityPerWeek: number;
  averageNumberOfInboundShipments: number;
  averageNumberOfOutboundShipments: number;
  comment: string;
};

type ServiceRateField = keyof Pick<
  ServiceRatesFormValues,
  | 'storagePricePerWeek'
  | 'palletReceiptAndPutAwayPrice'
  | 'palletPickingAndDespatchPrice'
  | 'palletLayerPickingAndDespatchPrice'
  | 'casePickingAndDespatchPrice'
  | 'subsequentCasePickingAndDespatchPrice'
  | 'itemPickingAndDespatchPrice'
  | 'subsequentItemPickingAndDespatchPrice'
  | 'containerHandlingPrice'
  | 'demurragePricePerDay'
  | 'palletReturnsPrice'
  | 'itemReturnsPrice'
  | 'caseReturnsPrice'
  | 'auxiliaryServicePricePerManHour'
>;

const SERVICE_RATE_FIELDS: { field: ServiceRateField; label: string }[] = [
  { field: 'storagePricePerWeek', label: 'Storage' },
  {
    field: 'palletReceiptAndPutAwayPrice',
    label: 'Receipt and putaway (per pallet)',
  },
  {
    field: 'palletPickingAndDespatchPrice',
    label: 'Pallet picking and despatch (per pallet)',
  },
  {
    field: 'palletLayerPickingAndDespatchPrice',
    label: 'Pallet layer picking and despatch (per layer)',
  },
  {
    field: 'casePickingAndDespatchPrice',
    label: 'Case picking and despatch (per case)',
  },
  {
    field: 'subsequentCasePickingAndDespatchPrice',
    label: 'Subsequent case picking (per case)',
  },
  {
    field: 'itemPickingAndDespatchPrice',
    label: 'Item picking and despatch (per item)',
  },
  {
    field: 'subsequentItemPickingAndDespatchPrice',
    label: 'Subsequent item picking (per item)',
  },
  {
    field: 'containerHandlingPrice',
    label: 'Container handling (per container)',
  },
  { field: 'demurragePricePerDay', label: 'Demurrage (per consignment day)' },
  { field: 'palletReturnsPrice', label: 'Pallet returns (per pallet)' },
  { field: 'itemReturnsPrice', label: 'Item returns (per item)' },
  { field: 'caseReturnsPrice', label: 'Case returns (per case)' },
  {
    field: 'auxiliaryServicePricePerManHour',
    label: 'Auxiliary services (per man-hour)',
  },
];

function findUnpricedInPlayServices(
  data: ServiceRatesFormValues,
  match: Match | null
): string[] {
  if (data.volumeBasedDiscount) return [];

  const unavailableServices = match?.unavailableServices || [];
  const requestedServices = data.requestedServices || [];
  const isInList = (list: string[], field: string) =>
    list.some((service) => service.toLowerCase() === field.toLowerCase());

  return SERVICE_RATE_FIELDS.filter(({ field }) => {
    const value = data[field];
    if (value !== null && value !== undefined) return false;
    if (isInList(unavailableServices, field)) return false;

    const offeredByLsp =
      match?.originalRates?.[field as keyof Match['originalRates']] != null;
    const requestedByShipper = isInList(requestedServices, field);
    return offeredByLsp || requestedByShipper;
  }).map(({ label }) => label);
}

function labelForInvalidProperty(invalidProperty: string): string {
  const fieldToken = invalidProperty.split(' ')[0];
  const match = SERVICE_RATE_FIELDS.find(
    ({ field }) => field.toLowerCase() === fieldToken.toLowerCase()
  );
  return match?.label ?? invalidProperty;
}

const BILLING_CYCLE_OPTIONS = [
  { label: 'Weekly', value: 'Weekly' },
  { label: 'Every Two Weeks', value: 'Every Two Weeks' },
  { label: 'Monthly', value: 'Monthly' },
];

const PAYMENT_TERMS_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
];

const validationSchema = Yup.object().shape({
  desiredStartDate: Yup.string().required('A desired start date is required.'),
  averageStorageQuantityPerWeek: Yup.number()
    .required('Average storage quantity per week is required.')
    .typeError('Average storage quantity per week must be a number')
    .moreThan(0, 'Average storage quantity per week must be greater than 0'),
  estimatedDurationInMonths: Yup.number()
    .required('An estimated duration is required.')
    .typeError('Estimated duration must be a number')
    .integer('Estimated duration must be a whole number')
    .moreThan(0, 'Estimated duration must be greater than 0'),
  averageNumberOfInboundShipments: Yup.number()
    .required('Average number of inbound shipments is required.')
    .typeError('Average number of inbound shipments must be a number')
    .integer('Average number of inbound shipments must be a whole number')
    .min(0, 'Average number of inbound shipments must be zero or greater'),
  averageNumberOfOutboundShipments: Yup.number()
    .required('Average number of outbound shipments is required.')
    .typeError('Average number of outbound shipments must be a number')
    .integer('Average number of outbound shipments must be a whole number')
    .min(0, 'Average number of outbound shipments must be zero or greater'),
  billingCycleFrequency: Yup.object().required('Billing cycle is required'),
  paymentTermsInDays: Yup.object().required('Payment terms are required'),
});

// Service rate row component
type ServiceRateRowProps = {
  label: string;
  fieldName: keyof ServiceRatesFormValues;
  warehousePrice: number | null;
  currencySymbol: string | undefined;
  match: Match | null;
  isOnShortlist: boolean;
  volumeBasedDiscount: boolean;
  control: ReturnType<typeof useForm<ServiceRatesFormValues>>['control'];
  requestedServices: string[];
  onRequestService: (fieldName: string, requested: boolean) => void;
};

function ServiceRateRow({
  label,
  fieldName,
  warehousePrice,
  currencySymbol,
  match,
  isOnShortlist,
  volumeBasedDiscount,
  control,
  requestedServices,
  onRequestService,
}: ServiceRateRowProps) {
  const proposedValue =
    match?.serviceRates?.[fieldName as keyof typeof match.serviceRates]
      ?.lspProposedValue;
  const acceptedValue =
    match?.serviceRates?.[fieldName as keyof typeof match.serviceRates]
      ?.acceptedValue;
  const isUnavailable = match?.unavailableServices?.includes(fieldName);
  const isRequested =
    requestedServices.includes(fieldName) ||
    match?.requestedServices?.includes(fieldName);
  const hasNoPrice =
    warehousePrice === null && !proposedValue && !acceptedValue;
  const isRateInPlay =
    warehousePrice !== null ||
    isRequested ||
    proposedValue != null ||
    acceptedValue != null;

  const displayValue = acceptedValue ?? proposedValue ?? warehousePrice;

  return (
    <div
      className={`grid grid-cols-4 gap-4 py-3 px-4 border-b border-gray-100 hover:bg-gray-50/50 transition-colors ${
        isRequested && hasNoPrice ? 'bg-blue-50/50' : 'even:bg-gray-50/30'
      }`}
    >
      <div className='flex items-center'>
        <span className='text-sm text-gray-700'>{label}</span>
      </div>
      <div className='flex items-center'>
        {isUnavailable ? (
          <span className='text-sm text-gray-400 italic'>Not available</span>
        ) : displayValue ? (
          <span className='text-sm font-semibold text-gray-900'>
            {currencySymbol}
            {displayValue.toLocaleString()}
          </span>
        ) : isRequested ? (
          <span className='text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded font-medium'>
            Price Requested
          </span>
        ) : (
          <span className='text-sm text-gray-400'>Not set</span>
        )}
      </div>
      <div>
        {isOnShortlist && !isUnavailable && isRateInPlay && (
          <Controller
            name={fieldName}
            control={control}
            render={({ field }) => (
              <Input
                name={fieldName}
                type='number'
                placeholder={
                  warehousePrice ? String(warehousePrice) : 'Propose price'
                }
                value={field.value?.toString() ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value ? Number(e.target.value) : null)
                }
                disabled={volumeBasedDiscount || match?.isActionRequiredByLsp}
              />
            )}
          />
        )}
        {isUnavailable && (
          <span className='text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded'>
            Unavailable
          </span>
        )}
      </div>
      <div className='flex items-center justify-center'>
        {isOnShortlist && !isUnavailable && hasNoPrice && !isRequested && (
          <button
            type='button'
            onClick={() => onRequestService(fieldName, true)}
            disabled={match?.isActionRequiredByLsp}
            className='text-xs text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed'
          >
            Request Price
          </button>
        )}
        {isOnShortlist && !isUnavailable && isRequested && hasNoPrice && (
          <button
            type='button'
            onClick={() => onRequestService(fieldName, false)}
            disabled={match?.isActionRequiredByLsp}
            className='text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            Cancel Request
          </button>
        )}
      </div>
    </div>
  );
}

// Rate preview row for non-engaged state (read-only)
type RatePreviewRowProps = {
  label: string;
  value: number | null;
  currencySymbol: string | undefined;
};

function RatePreviewRow({ label, value, currencySymbol }: RatePreviewRowProps) {
  return (
    <div className='flex items-center justify-between py-3 px-5 hover:bg-gray-50/50 transition-colors'>
      <span className='text-sm text-gray-700'>{label}</span>
      {value ? (
        <span className='text-sm font-semibold text-gray-900'>
          {currencySymbol}
          {value.toLocaleString()}
        </span>
      ) : (
        <span className='text-sm text-gray-400'>—</span>
      )}
    </div>
  );
}

export function ClientServiceRates() {
  const { warehouseId } = useParams<{ warehouseId: string }>();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('matchId');

  const navigate = useNavigate();
  const authHttp = useAuthHttp();
  const { user } = useAuth();
  const userRoleService = UserRoleService();

  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [match, setMatch] = useAtom(matchAtom);
  const [isLoading, setIsLoading] = useState(true);
  const hasFetched = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serviceRateWarnings, setServiceRateWarnings] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isAddingToShortlist, setIsAddingToShortlist] = useState(false);
  const [, setConfirmationMessage] = useAtom(confirmationMessageAtom);
  const [businessProfile, setBusinessProfile] = useAtom(businessProfileAtom);
  const [matchSigningDetails, setMatchSigningDetails] = useState<{
    isSigned: boolean;
    signedAt: string;
    signedByEmail: string;
    signingUrl: string;
  } | null>(null);

  const hasCompletedBusinessProfile =
    businessProfile &&
    businessProfile?.companyNumber &&
    businessProfile?.address &&
    businessProfile?.contactEmail &&
    businessProfile?.contactName;

  const currencies = SUPPORTED_CURRENCY_GROUPS.flatMap(
    (currencyGroup) => currencyGroup.currencies
  );

  const currencySymbol = warehouse?.currency
    ? currencies.find((currency) => currency.code === warehouse.currency)
        ?.symbol
    : '£';

  const isOnShortlist = !!match;

  const pages = [
    { name: 'Warehouses', href: '/warehouse-search', current: false },
    {
      name: warehouse?.name || 'Warehouse',
      href: `/warehouse/${warehouseId}`,
      current: false,
    },
    { name: 'Service Rates', href: '#', current: true },
  ];

  const defaultValues: ServiceRatesFormValues = {
    storagePricePerWeek: null,
    palletReceiptAndPutAwayPrice: null,
    palletPickingAndDespatchPrice: null,
    palletLayerPickingAndDespatchPrice: null,
    casePickingAndDespatchPrice: null,
    subsequentCasePickingAndDespatchPrice: null,
    itemPickingAndDespatchPrice: null,
    subsequentItemPickingAndDespatchPrice: null,
    containerHandlingPrice: null,
    demurragePricePerDay: null,
    palletReturnsPrice: null,
    itemReturnsPrice: null,
    caseReturnsPrice: null,
    auxiliaryServicePricePerManHour: null,
    requestedServices: [],
    volumeBasedDiscount: false,
    monthlyTurnoverOne: 0,
    monthlyDiscountOne: 0,
    monthlyTurnoverTwo: 0,
    monthlyDiscountTwo: 0,
    monthlyTurnoverThree: 0,
    monthlyDiscountThree: 0,
    billingCycleFrequency: BILLING_CYCLE_OPTIONS[2],
    paymentTermsInDays: PAYMENT_TERMS_OPTIONS[2],
    desiredStartDate: dayjs().format('YYYY-MM-DD'),
    estimatedDurationInMonths: 0,
    averageStorageQuantityPerWeek: 0,
    averageNumberOfInboundShipments: 0,
    averageNumberOfOutboundShipments: 0,
    comment: '',
  };

  const methods = useForm<ServiceRatesFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues,
  });

  const { control, watch, reset, setValue } = methods;

  const volumeBasedDiscount = watch('volumeBasedDiscount');
  const requestedServices = watch('requestedServices');

  const handleRequestService = (fieldName: string, requested: boolean) => {
    const current = requestedServices || [];
    if (requested) {
      setValue('requestedServices', [...current, fieldName]);
    } else {
      setValue(
        'requestedServices',
        current.filter((s) => s !== fieldName)
      );
    }
  };

  // Common props for ServiceRateRow components
  const serviceRateRowCommonProps = {
    currencySymbol,
    match,
    isOnShortlist,
    volumeBasedDiscount,
    control,
    requestedServices: requestedServices || [],
    onRequestService: handleRequestService,
  };

  // Fetch warehouse data
  const fetchWarehouse = useCallback(async () => {
    if (!warehouseId) return;

    try {
      const { data } = await authHttp.get(
        `${WAREHOUSE_SEARCH_GET}/${warehouseId}`
      );
      if (data?.warehouse) {
        setWarehouse(data.warehouse);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching warehouse:', error);
    }
  }, [warehouseId, authHttp]);

  // Fetch match data
  const fetchMatch = useCallback(async () => {
    if (!warehouseId) return;

    try {
      const { data } = await authHttp.post(
        MATCH_LIST,
        {
          warehouseId,
          matchId: matchId || null,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (data?.matches && data.matches.length > 0) {
        const warehouseMatch = matchId
          ? data.matches.find(
              (m: { match: { id: string } }) => m.match.id === matchId
            )
          : data.matches.find(
              (m: { match: { matchStatus: string } }) =>
                m.match.matchStatus !== 'Rejected'
            );
        if (warehouseMatch) {
          setMatch(warehouseMatch.match);
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching match:', error);
    }
  }, [warehouseId, matchId, authHttp, setMatch]);

  // Fetch business profile
  const fetchBusinessProfile = useCallback(async () => {
    try {
      const { data } = await authHttp.get(BUSINESS_PROFILE_GET);
      if (data?.businessProfile) {
        setBusinessProfile(data.businessProfile);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching business profile:', error);
    }
  }, [authHttp, setBusinessProfile]);

  // Fetch signing details
  const fetchSigningDetails = useCallback(async () => {
    if (!match?.id) return;

    try {
      const { data } = await authHttp.get(GET_MATCH_SIGNING_DETAILS, {
        params: { matchId: match.id },
      });
      if (data) {
        setMatchSigningDetails(data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching signing details:', error);
    }
  }, [match?.id, authHttp]);

  // Initial data fetch
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchWarehouse(),
        fetchMatch(),
        fetchBusinessProfile(),
      ]);
      setIsLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch signing details when the negotiation has concluded.
  // Keyed on stable values only — fetchSigningDetails changes identity every
  // render (useAuthHttp returns a fresh instance), so depending on it refires
  // the effect after each fetch and loops forever.
  useEffect(() => {
    if (
      match?.matchStatus === 'Reviewing T&Cs' ||
      match?.matchStatus === 'Contracted'
    ) {
      fetchSigningDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.matchStatus, match?.id]);

  // Populate form with match data
  useEffect(() => {
    if (match && warehouse) {
      const desiredStartDate =
        match.desiredStartDate === null ||
        dayjs(match.desiredStartDate).isBefore(dayjs())
          ? dayjs().format('YYYY-MM-DD')
          : dayjs(match.desiredStartDate).format('YYYY-MM-DD');

      const billingOption = BILLING_CYCLE_OPTIONS.find(
        (opt) => opt.value === match.billingCycleFrequency
      );
      const paymentOption = PAYMENT_TERMS_OPTIONS.find(
        (opt) => opt.value === match.paymentTermsInDays
      );

      reset({
        storagePricePerWeek: !match?.unavailableServices.includes(
          'storagePricePerWeek'
        )
          ? match.serviceRates?.storagePricePerWeek?.shipperProposedValue ??
            match.originalRates.storagePricePerWeek
          : null,
        palletReceiptAndPutAwayPrice: !match?.unavailableServices.includes(
          'palletReceiptAndPutAwayPrice'
        )
          ? match.serviceRates?.palletReceiptAndPutAwayPrice
              ?.shipperProposedValue ??
            match.originalRates.palletReceiptAndPutAwayPrice
          : null,
        palletPickingAndDespatchPrice: !match?.unavailableServices.includes(
          'palletPickingAndDespatchPrice'
        )
          ? match.serviceRates?.palletPickingAndDespatchPrice
              ?.shipperProposedValue ??
            match.originalRates.palletPickingAndDespatchPrice
          : null,
        palletLayerPickingAndDespatchPrice:
          !match?.unavailableServices.includes(
            'palletLayerPickingAndDespatchPrice'
          )
            ? match.serviceRates?.palletLayerPickingAndDespatchPrice
                ?.shipperProposedValue ??
              match.originalRates.palletLayerPickingAndDespatchPrice
            : null,
        casePickingAndDespatchPrice: !match?.unavailableServices.includes(
          'casePickingAndDespatchPrice'
        )
          ? match.serviceRates?.casePickingAndDespatchPrice
              ?.shipperProposedValue ??
            match.originalRates.casePickingAndDespatchPrice
          : null,
        subsequentCasePickingAndDespatchPrice:
          !match?.unavailableServices.includes(
            'subsequentCasePickingAndDespatchPrice'
          )
            ? match.serviceRates?.subsequentCasePickingAndDespatchPrice
                ?.shipperProposedValue ??
              match.originalRates.subsequentCasePickingAndDespatchPrice
            : null,
        itemPickingAndDespatchPrice: !match?.unavailableServices.includes(
          'itemPickingAndDespatchPrice'
        )
          ? match.serviceRates?.itemPickingAndDespatchPrice
              ?.shipperProposedValue ??
            match.originalRates.itemPickingAndDespatchPrice
          : null,
        subsequentItemPickingAndDespatchPrice:
          !match?.unavailableServices.includes(
            'subsequentItemPickingAndDespatchPrice'
          )
            ? match.serviceRates?.subsequentItemPickingAndDespatchPrice
                ?.shipperProposedValue ??
              match.originalRates.subsequentItemPickingAndDespatchPrice
            : null,
        containerHandlingPrice: !match?.unavailableServices.includes(
          'containerHandlingPrice'
        )
          ? match.serviceRates?.containerHandlingPrice?.shipperProposedValue ??
            match.originalRates.containerHandlingPrice
          : null,
        demurragePricePerDay: !match?.unavailableServices.includes(
          'demurragePricePerDay'
        )
          ? match.serviceRates?.demurragePricePerDay?.shipperProposedValue ??
            match.originalRates.demurragePricePerDay
          : null,
        palletReturnsPrice: !match?.unavailableServices.includes(
          'palletReturnsPrice'
        )
          ? match.serviceRates?.palletReturnsPrice?.shipperProposedValue ??
            match.originalRates.palletReturnsPrice
          : null,
        itemReturnsPrice: !match?.unavailableServices.includes(
          'itemReturnsPrice'
        )
          ? match.serviceRates?.itemReturnsPrice?.shipperProposedValue ??
            match.originalRates.itemReturnsPrice
          : null,
        caseReturnsPrice: !match?.unavailableServices.includes(
          'caseReturnsPrice'
        )
          ? match.serviceRates?.caseReturnsPrice?.shipperProposedValue ??
            match.originalRates.caseReturnsPrice
          : null,
        auxiliaryServicePricePerManHour: !match?.unavailableServices.includes(
          'auxiliaryServicePricePerManHour'
        )
          ? match.serviceRates?.auxiliaryServicePricePerManHour
              ?.shipperProposedValue ??
            match.originalRates.auxiliaryServicePricePerManHour
          : null,
        requestedServices: match.requestedServices || [],
        volumeBasedDiscount: match.volumeDiscounts?.length > 0,
        monthlyTurnoverOne:
          match.volumeDiscounts?.[0]?.maxTotalMonthlyTurnover || 0,
        monthlyDiscountOne: match.isActionRequiredByLsp
          ? match.volumeDiscounts?.[0]?.shipperProposedDiscountPercentage || 0
          : match.volumeDiscounts?.[0]?.lspProposedDiscountPercentage || 0,
        monthlyTurnoverTwo:
          match.volumeDiscounts?.[1]?.maxTotalMonthlyTurnover || 0,
        monthlyDiscountTwo: match.isActionRequiredByLsp
          ? match.volumeDiscounts?.[1]?.shipperProposedDiscountPercentage || 0
          : match.volumeDiscounts?.[1]?.lspProposedDiscountPercentage || 0,
        monthlyTurnoverThree:
          match.volumeDiscounts?.[2]?.maxTotalMonthlyTurnover || 0,
        monthlyDiscountThree: match.isActionRequiredByLsp
          ? match.volumeDiscounts?.[2]?.shipperProposedDiscountPercentage || 0
          : match.volumeDiscounts?.[2]?.lspProposedDiscountPercentage || 0,
        billingCycleFrequency: billingOption || BILLING_CYCLE_OPTIONS[2],
        paymentTermsInDays: paymentOption || PAYMENT_TERMS_OPTIONS[2],
        desiredStartDate,
        estimatedDurationInMonths: match.estimatedDurationInMonths || 0,
        averageStorageQuantityPerWeek: match.averageStorageQuantityPerWeek || 0,
        averageNumberOfInboundShipments:
          match.averageNumberOfInboundShipmentsPerWeek || 0,
        averageNumberOfOutboundShipments:
          match.averageNumberOfOutboundShipmentsPerWeek || 0,
        comment: '',
      });
    }
  }, [match, warehouse, reset]);

  // Add to shortlist handler
  const handleAddToShortlist = async () => {
    if (!warehouseId) return;

    setIsAddingToShortlist(true);
    try {
      const { data } = await authHttp.post(
        ADD_WAREHOUSE_MATCH,
        {
          match: { warehouseId },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data?.match) {
        setMatch(data.match);
        setConfirmationMessage('Successfully added to shortlist');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding to shortlist:', error);
    } finally {
      setIsAddingToShortlist(false);
    }
  };

  // Submit proposal handler
  const onSubmit = async (data: ServiceRatesFormValues) => {
    if (!businessProfile) {
      navigate('/profile');
      return;
    }

    const unpricedServices = findUnpricedInPlayServices(data, match);
    if (unpricedServices.length > 0) {
      setServiceRateWarnings(unpricedServices);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setServiceRateWarnings([]);
    setSubmitError(null);

    setIsSubmitting(true);
    try {
      const { data: proposal } = await authHttp.post(
        PROPOSE_WAREHOUSE_MATCH_DETAILS,
        {
          match: {
            matchId: match?.id,
            desiredStartDate: dayjs(data.desiredStartDate).format('YYYY-MM-DD'),
            averageStorageQuantityPerWeek: data.averageStorageQuantityPerWeek,
            estimatedDurationInMonths: data.estimatedDurationInMonths,
            averageNumberOfInboundShipmentsPerWeek:
              data.averageNumberOfInboundShipments,
            averageNumberOfOutboundShipmentsPerWeek:
              data.averageNumberOfOutboundShipments,
            shipperIndustry:
              userRoleService.isWarehouseClient(user) &&
              businessProfile?.industry
                ? businessProfile.industry
                : '',
            requestedServices: data.requestedServices || [],
            unavailableServices: match?.unavailableServices || [],
            serviceRates: data.volumeBasedDiscount
              ? null
              : {
                  storagePricePerWeek: data.storagePricePerWeek,
                  palletReceiptAndPutAwayPrice:
                    data.palletReceiptAndPutAwayPrice,
                  palletPickingAndDespatchPrice:
                    data.palletPickingAndDespatchPrice,
                  palletLayerPickingAndDespatchPrice:
                    data.palletLayerPickingAndDespatchPrice,
                  casePickingAndDespatchPrice: data.casePickingAndDespatchPrice,
                  subsequentCasePickingAndDespatchPrice:
                    data.subsequentCasePickingAndDespatchPrice,
                  itemPickingAndDespatchPrice: data.itemPickingAndDespatchPrice,
                  subsequentItemPickingAndDespatchPrice:
                    data.subsequentItemPickingAndDespatchPrice,
                  containerHandlingPrice: data.containerHandlingPrice,
                  demurragePricePerDay: data.demurragePricePerDay,
                  palletReturnsPrice: data.palletReturnsPrice,
                  itemReturnsPrice: data.itemReturnsPrice,
                  caseReturnsPrice: data.caseReturnsPrice,
                  auxiliaryServicePricePerManHour:
                    data.auxiliaryServicePricePerManHour,
                },
            volumeDiscounts: data.volumeBasedDiscount
              ? [
                  {
                    id: match?.volumeDiscounts?.[0]?.id,
                    maxTotalMonthlyTurnover: data.monthlyTurnoverOne,
                    minTotalMonthlyTurnover: 0,
                    discountPercentage: data.monthlyDiscountOne,
                  },
                  ...(data.monthlyTurnoverTwo && data.monthlyDiscountTwo
                    ? [
                        {
                          id: match?.volumeDiscounts?.[1]?.id,
                          maxTotalMonthlyTurnover: data.monthlyTurnoverTwo,
                          minTotalMonthlyTurnover: data.monthlyTurnoverOne + 1,
                          discountPercentage: data.monthlyDiscountTwo,
                        },
                      ]
                    : []),
                  ...(data.monthlyTurnoverTwo && data.monthlyDiscountThree
                    ? [
                        {
                          id: match?.volumeDiscounts?.[2]?.id,
                          minTotalMonthlyTurnover: data.monthlyTurnoverTwo + 1,
                          discountPercentage: data.monthlyDiscountThree,
                        },
                      ]
                    : []),
                ]
              : null,
            billingCycleFrequency: data.billingCycleFrequency.value,
            paymentTermsInDays: data.paymentTermsInDays.value,
            comment: data.comment,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (proposal) {
        setMatch(proposal.match);
        setConfirmationMessage('Proposal submitted successfully');
        navigate('/warehouse-shortlist');
      }
    } catch (error) {
      const firstError = (
        error as {
          response?: {
            data?: {
              errors?: { attemptedValue?: unknown; errorMessage?: string }[];
            };
          };
        }
      )?.response?.data?.errors?.[0];
      const invalidProperties = firstError?.attemptedValue;
      if (Array.isArray(invalidProperties) && invalidProperties.length > 0) {
        setServiceRateWarnings(
          invalidProperties.map((entry) =>
            labelForInvalidProperty(String(entry))
          )
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitError(
          firstError?.errorMessage ||
            'Something went wrong submitting your proposal. Please try again.'
        );
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // eslint-disable-next-line no-console
      console.error('Error submitting proposal:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Accept match handler
  const handleAcceptMatch = async () => {
    if (!businessProfile?.companyNumber || !businessProfile?.address) {
      setConfirmationMessage(
        'Add your company number and business address to your profile before accepting the match.'
      );
      navigate('/profile');
      return;
    }

    setSubmitError(null);
    setIsAccepting(true);
    try {
      const { data: proposal } = await authHttp.post(
        ACCEPT_WAREHOUSE_MATCH,
        {
          match: { matchId: match?.id },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (proposal) {
        setMatch(proposal.match);
        setConfirmationMessage('Match accepted successfully');
        navigate('/warehouse-shortlist');
      }
    } catch (error) {
      const errorMessage = (
        error as {
          response?: { data?: { errors?: { errorMessage?: string }[] } };
        }
      )?.response?.data?.errors?.[0]?.errorMessage;
      setSubmitError(
        errorMessage ||
          'Something went wrong accepting the rates. Please try again.'
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // eslint-disable-next-line no-console
      console.error('Error accepting match:', error);
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <div className='flex flex-col items-center gap-3'>
          <FloxSpinner size='md' />
          <p className='text-gray-500 text-sm'>Loading service rates...</p>
        </div>
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className='flex items-center justify-center h-screen'>
        <p className='text-gray-500'>Warehouse not found</p>
      </div>
    );
  }

  // If not on shortlist, show add to shortlist view with warehouse rates
  if (!isOnShortlist) {
    return (
      <div className='h-[calc(100vh-64px)] flex flex-col bg-gray-50'>
        <PageHeader header='Service Rates' pages={pages} />
        <div className='flex-1 overflow-auto px-10 py-6'>
          <div className='max-w-4xl mx-auto'>
            {/* Engage CTA Card */}
            <div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6'>
              <div className='flex items-start gap-4'>
                <div className='w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0'>
                  <svg
                    className='w-6 h-6 text-blue-600'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={1.5}
                      d='M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z'
                    />
                  </svg>
                </div>
                <div className='flex-1'>
                  <h2 className='text-lg font-semibold text-gray-900 mb-1'>
                    Start a conversation with{' '}
                    {warehouse.userBusinessName || warehouse.name}
                  </h2>
                  <p className='text-gray-600 text-sm mb-4'>
                    Engaging with this warehouse will add them to your shortlist
                    and open up rate negotiations. You&apos;ll be able to
                    propose your own rates and terms.
                  </p>
                  <Button
                    label={
                      isAddingToShortlist ? 'Starting...' : 'Engage Warehouse'
                    }
                    intent='primary'
                    size='sm'
                    onClick={handleAddToShortlist}
                    disabled={isAddingToShortlist}
                  />
                </div>
              </div>
            </div>

            {/* Warehouse Rates Preview */}
            <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
              <div className='px-5 py-4 border-b border-gray-200 bg-gray-50'>
                <h3 className='text-sm font-semibold text-gray-900'>
                  Warehouse Published Rates
                </h3>
                <p className='text-xs text-gray-500 mt-1'>
                  These are the standard rates published by the warehouse. Once
                  engaged, you can negotiate custom rates.
                </p>
              </div>

              {/* Rate Rows */}
              <div className='divide-y divide-gray-100'>
                <RatePreviewRow
                  label={`Storage (per ${
                    warehouse.storageCapacityUnitOfMeasure === 'Pallets'
                      ? 'pallet-week'
                      : warehouse.storageCapacityUnitOfMeasure?.toLowerCase()
                  })`}
                  value={
                    warehouse.storageCapacityUnitOfMeasure === 'Pallets'
                      ? warehouse.palletStoragePricePerWeek
                      : warehouse.storageCapacityUnitOfMeasure ===
                        'Square Metre'
                      ? warehouse.squareMetreStoragePricePerWeek
                      : warehouse.squareFeetStoragePricePerWeek
                  }
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Receipt and putaway (per pallet)'
                  value={warehouse.palletReceiptAndPutAwayPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Pallet picking and despatch (per pallet)'
                  value={warehouse.palletPickingAndDespatchPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Pallet layer picking and despatch (per layer)'
                  value={warehouse.palletLayerPickingAndDespatchPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Case picking and despatch (per case)'
                  value={warehouse.casePickingAndDespatchPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Subsequent case picking (per case)'
                  value={warehouse.subsequentCasePickingAndDespatchPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Item picking and despatch (per item)'
                  value={warehouse.itemPickingAndDespatchPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Subsequent item picking (per item)'
                  value={warehouse.subsequentItemPickingAndDespatchPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Container handling (per container)'
                  value={warehouse.containerHandlingPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Demurrage (per consignment day)'
                  value={warehouse.demurragePricePerDay}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Pallet returns (per pallet)'
                  value={warehouse.palletReturnsPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Item returns (per item)'
                  value={warehouse.itemReturnsPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Case returns (per case)'
                  value={warehouse.caseReturnsPrice}
                  currencySymbol={currencySymbol}
                />
                <RatePreviewRow
                  label='Auxiliary services (per man-hour)'
                  value={warehouse.auxiliaryServicePricePerManHour}
                  currencySymbol={currencySymbol}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (match?.matchStatus === 'Reviewing T&Cs') {
    return (
      <div className='h-[calc(100vh-64px)] flex flex-col bg-gray-50'>
        <PageHeader header='Contract Signing' pages={pages} />
        <div className='flex-1 flex flex-col items-center justify-center px-10 py-6'>
          <div className='max-w-md text-center bg-white rounded-xl shadow-sm border border-gray-200 p-8'>
            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6'>
              <svg
                className='w-8 h-8 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <h2 className='text-xl font-semibold text-gray-900 mb-3'>
              Rates Agreed
            </h2>
            <p className='text-gray-600 mb-6 text-sm leading-relaxed'>
              You and {warehouse.userBusinessName || warehouse.name} have agreed
              on the rates — the negotiation is complete and no further changes
              can be made. Please sign the Pricing Schedule Addendum to complete
              the contract.
            </p>
            {matchSigningDetails?.isSigned === false && (
              <Button
                label='Sign Pricing Schedule'
                className='h-auto'
                onClick={() =>
                  window.open(matchSigningDetails.signingUrl, '_blank')
                }
              />
            )}
            {matchSigningDetails?.isSigned === true && (
              <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
                <p className='text-sm text-amber-700'>
                  You have signed. Waiting for the other party to sign.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (match?.matchStatus === 'Contracted') {
    return (
      <div className='h-[calc(100vh-64px)] flex flex-col bg-gray-50'>
        <PageHeader header='Contract' pages={pages} />
        <div className='flex-1 flex flex-col items-center justify-center px-10 py-6'>
          <div className='max-w-md text-center bg-white rounded-xl shadow-sm border border-gray-200 p-8'>
            <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6'>
              <svg
                className='w-8 h-8 text-green-600'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
            </div>
            <h2 className='text-xl font-semibold text-gray-900 mb-3'>
              Contract Active
            </h2>
            <p className='text-gray-600 mb-6 text-sm leading-relaxed'>
              This contract is now active with{' '}
              {warehouse.userBusinessName || warehouse.name}.
            </p>
            <Button
              label='View Contract'
              className='h-auto'
              onClick={() =>
                window.open(match.legalDocuments?.[0]?.url, '_blank')
              }
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormPage<ServiceRatesFormValues>
      header='Service Rates'
      pages={pages}
      methods={methods}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
      submitText='Send Proposal'
      submitError={submitError}
      onDismissError={() => setSubmitError(null)}
      customActions={
        match?.isActionRequiredByShipper ? (
          <Button
            label={isAccepting ? 'Accepting...' : 'Accept Proposed Rates'}
            intent='primary'
            size='sm'
            onClick={handleAcceptMatch}
            disabled={isSubmitting || isAccepting}
            type='button'
          />
        ) : undefined
      }
    >
      <div className='flex gap-6'>
        {/* Main Content - Left Side */}
        <div className='flex-1 min-w-0'>
          {serviceRateWarnings.length > 0 && (
            <div className='p-4 mb-6 bg-red-50 border border-red-300 rounded-xl flex items-start gap-3 shadow-sm'>
              <div className='w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0'>
                <svg
                  className='w-5 h-5 text-red-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z'
                  />
                </svg>
              </div>
              <div>
                <p className='text-sm font-semibold text-red-900'>
                  Add a proposed price for these services before sending your
                  proposal
                </p>
                <ul className='mt-2 list-disc list-inside space-y-0.5'>
                  {serviceRateWarnings.map((warning) => (
                    <li key={warning} className='text-sm text-red-700'>
                      {warning}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Waiting for LSP response banner */}
          {match?.isActionRequiredByLsp && (
            <div className='p-4 mb-6 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3 shadow-sm'>
              <div className='w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0'>
                <svg
                  className='w-5 h-5 text-amber-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </div>
              <div>
                <p className='text-sm font-semibold text-amber-900'>
                  Awaiting response from{' '}
                  {warehouse.userBusinessName || warehouse.name}
                </p>
                <p className='text-sm text-amber-700 mt-1'>
                  Your proposal has been submitted. The warehouse is reviewing
                  your rates and will respond with a counter-proposal or
                  acceptance.
                </p>
              </div>
            </div>
          )}

          {!hasCompletedBusinessProfile && (
            <div className='p-4 mb-6 bg-sky-50 border border-sky-200 rounded-xl flex items-start gap-3'>
              <div className='w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center flex-shrink-0'>
                <svg
                  className='w-5 h-5 text-sky-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                  />
                </svg>
              </div>
              <div>
                <p className='text-sm font-semibold text-sky-900'>
                  Complete your business profile
                </p>
                <p className='text-sm text-sky-700 mt-1'>
                  Before you can negotiate or accept rates, please{' '}
                  <a
                    href='/profile'
                    className='text-blue-600 underline font-medium hover:text-blue-700'
                  >
                    complete your Business Profile
                  </a>
                  .
                </p>
              </div>
            </div>
          )}

          {/* Section Header */}
          <div className='mb-6'>
            <h2 className='text-xl font-semibold text-gray-900'>
              {match?.matchStatus === 'Reviewing T&Cs' ||
              match?.matchStatus === 'Contracted'
                ? 'Agreed Service Rates'
                : 'Negotiate Service Rates'}
            </h2>
            <p className='text-sm text-gray-500 mt-1'>
              Review the warehouse&apos;s standard rates and propose your own if
              needed
            </p>
          </div>

          {/* Service Rates Card */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
            {/* Table Header */}
            <div className='grid grid-cols-4 gap-4 py-3 px-4 bg-gray-50 border-b border-gray-200'>
              <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                Service
              </span>
              <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                LSP Rate
              </span>
              <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                Your Proposal
              </span>
              <span className='text-xs font-semibold text-gray-500 uppercase tracking-wide text-center'>
                Request
              </span>
            </div>

            {/* Service Rate Rows */}
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label={`Storage (per ${
                warehouse.storageCapacityUnitOfMeasure === 'Pallets'
                  ? 'pallet-week'
                  : warehouse.storageCapacityUnitOfMeasure?.toLowerCase()
              })`}
              fieldName='storagePricePerWeek'
              warehousePrice={
                warehouse.storageCapacityUnitOfMeasure === 'Pallets'
                  ? warehouse.palletStoragePricePerWeek
                  : warehouse.storageCapacityUnitOfMeasure === 'Square Metre'
                  ? warehouse.squareMetreStoragePricePerWeek
                  : warehouse.squareFeetStoragePricePerWeek
              }
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Receipt and putaway (per pallet)'
              fieldName='palletReceiptAndPutAwayPrice'
              warehousePrice={warehouse.palletReceiptAndPutAwayPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Pallet picking and despatch (per pallet)'
              fieldName='palletPickingAndDespatchPrice'
              warehousePrice={warehouse.palletPickingAndDespatchPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Pallet layer picking and despatch (per layer)'
              fieldName='palletLayerPickingAndDespatchPrice'
              warehousePrice={warehouse.palletLayerPickingAndDespatchPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Case picking and despatch (per case)'
              fieldName='casePickingAndDespatchPrice'
              warehousePrice={warehouse.casePickingAndDespatchPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Subsequent case picking (per case)'
              fieldName='subsequentCasePickingAndDespatchPrice'
              warehousePrice={warehouse.subsequentCasePickingAndDespatchPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Item picking and despatch (per item)'
              fieldName='itemPickingAndDespatchPrice'
              warehousePrice={warehouse.itemPickingAndDespatchPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Subsequent item picking (per item)'
              fieldName='subsequentItemPickingAndDespatchPrice'
              warehousePrice={warehouse.subsequentItemPickingAndDespatchPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Container handling (per container)'
              fieldName='containerHandlingPrice'
              warehousePrice={warehouse.containerHandlingPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Demurrage (per consignment day)'
              fieldName='demurragePricePerDay'
              warehousePrice={warehouse.demurragePricePerDay}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Pallet returns (per pallet)'
              fieldName='palletReturnsPrice'
              warehousePrice={warehouse.palletReturnsPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Item returns (per item)'
              fieldName='itemReturnsPrice'
              warehousePrice={warehouse.itemReturnsPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Case returns (per case)'
              fieldName='caseReturnsPrice'
              warehousePrice={warehouse.caseReturnsPrice}
            />
            <ServiceRateRow
              {...serviceRateRowCommonProps}
              label='Auxiliary services (per man-hour)'
              fieldName='auxiliaryServicePricePerManHour'
              warehousePrice={warehouse.auxiliaryServicePricePerManHour}
            />
          </div>

          {/* Volume Based Discount */}
          <div className='mt-6 bg-white rounded-xl border border-gray-200 shadow-sm p-5'>
            <Controller
              name='volumeBasedDiscount'
              control={control}
              render={({ field }) => (
                <Checkbox
                  name={field.name}
                  label='Apply volume-based discount structure instead of per-service rates'
                  checked={field.value}
                  onChange={(e) => {
                    setValue('volumeBasedDiscount', e.target.checked, {
                      shouldValidate: true,
                    });
                  }}
                  disabled={match?.isActionRequiredByLsp}
                />
              )}
            />

            {volumeBasedDiscount && (
              <div className='mt-6 space-y-4 pt-4 border-t border-gray-100'>
                <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide'>
                  Volume Discount Tiers
                </h3>

                {/* Tier 1 */}
                <div className='flex items-center gap-4 p-4 bg-blue-50/50 rounded-lg border border-blue-100'>
                  <div className='w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0'>
                    <span className='text-sm font-bold text-blue-600'>1</span>
                  </div>
                  <div className='flex-1 grid grid-cols-2 gap-4'>
                    <Controller
                      name='monthlyTurnoverOne'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Up to monthly turnover'
                          name='monthlyTurnoverOne'
                          type='number'
                          placeholder={`${currencySymbol}5,000`}
                          value={field.value?.toString() || ''}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                          disabled={match?.isActionRequiredByLsp}
                        />
                      )}
                    />
                    <Controller
                      name='monthlyDiscountOne'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Discount %'
                          name='monthlyDiscountOne'
                          type='number'
                          placeholder='5'
                          value={field.value?.toString() || ''}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                          disabled={match?.isActionRequiredByLsp}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Tier 2 */}
                <div className='flex items-center gap-4 p-4 bg-green-50/50 rounded-lg border border-green-100'>
                  <div className='w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0'>
                    <span className='text-sm font-bold text-green-600'>2</span>
                  </div>
                  <div className='flex-1 grid grid-cols-2 gap-4'>
                    <Controller
                      name='monthlyTurnoverTwo'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Up to monthly turnover'
                          name='monthlyTurnoverTwo'
                          type='number'
                          placeholder={`${currencySymbol}15,000`}
                          value={field.value?.toString() || ''}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                          disabled={match?.isActionRequiredByLsp}
                        />
                      )}
                    />
                    <Controller
                      name='monthlyDiscountTwo'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Discount %'
                          name='monthlyDiscountTwo'
                          type='number'
                          placeholder='10'
                          value={field.value?.toString() || ''}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                          disabled={match?.isActionRequiredByLsp}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Tier 3 */}
                <div className='flex items-center gap-4 p-4 bg-purple-50/50 rounded-lg border border-purple-100'>
                  <div className='w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0'>
                    <span className='text-sm font-bold text-purple-600'>3</span>
                  </div>
                  <div className='flex-1 grid grid-cols-2 gap-4'>
                    <div className='flex items-end pb-2'>
                      <span className='text-sm text-gray-600 font-medium'>
                        Above tier 2
                      </span>
                    </div>
                    <Controller
                      name='monthlyDiscountThree'
                      control={control}
                      render={({ field }) => (
                        <Input
                          label='Discount %'
                          name='monthlyDiscountThree'
                          type='number'
                          placeholder='15'
                          value={field.value?.toString() || ''}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                          disabled={match?.isActionRequiredByLsp}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Your Commitment */}
          <div className='mt-10 mb-6'>
            <h2 className='text-xl font-semibold text-gray-900'>
              Your Commitment
            </h2>
            <p className='text-sm text-gray-500 mt-1'>
              Define your expected storage needs and payment preferences
            </p>
          </div>

          {/* Payment Terms Card */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6'>
            <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2'>
              <svg
                className='w-4 h-4 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z'
                />
              </svg>
              Payment Terms
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Controller
                name='billingCycleFrequency'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Billing Cycle'
                    options={BILLING_CYCLE_OPTIONS}
                    selected={field.value}
                    setSelected={field.onChange}
                  />
                )}
              />
              <Controller
                name='paymentTermsInDays'
                control={control}
                render={({ field }) => (
                  <Select
                    label='Payment Terms'
                    options={PAYMENT_TERMS_OPTIONS}
                    selected={field.value}
                    setSelected={field.onChange}
                  />
                )}
              />
            </div>
          </div>

          {/* Contract Duration Card */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6'>
            <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2'>
              <svg
                className='w-4 h-4 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
                />
              </svg>
              Contract Duration
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <Controller
                name='desiredStartDate'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Datepicker
                    label='Desired Start Date'
                    name='desiredStartDate'
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    startDate={dayjs().format('YYYY-MM-DD')}
                    disabled={match?.isActionRequiredByLsp}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
              <Controller
                name='estimatedDurationInMonths'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Estimated Duration (months)'
                    name='estimatedDurationInMonths'
                    type='number'
                    step={1}
                    min={1}
                    placeholder='12'
                    value={field.value?.toString() || ''}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    disabled={match?.isActionRequiredByLsp}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
            </div>
          </div>

          {/* Volume Estimates Card */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6'>
            <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2'>
              <svg
                className='w-4 h-4 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
                />
              </svg>
              Volume Estimates
            </h3>
            <div className='grid grid-cols-1 md:grid-cols-3 gap-6'>
              <Controller
                name='averageStorageQuantityPerWeek'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label={`Storage (${warehouse.storageCapacityUnitOfMeasure?.toLowerCase()}/week)`}
                    name='averageStorageQuantityPerWeek'
                    type='number'
                    placeholder='100'
                    value={field.value?.toString() || ''}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    disabled={match?.isActionRequiredByLsp}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
              <Controller
                name='averageNumberOfInboundShipments'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Inbound (per week)'
                    name='averageNumberOfInboundShipments'
                    type='number'
                    step={1}
                    min={0}
                    placeholder='50'
                    value={field.value?.toString() || ''}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    disabled={match?.isActionRequiredByLsp}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
              <Controller
                name='averageNumberOfOutboundShipments'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    label='Outbound (per week)'
                    name='averageNumberOfOutboundShipments'
                    type='number'
                    step={1}
                    min={0}
                    placeholder='50'
                    value={field.value?.toString() || ''}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    disabled={match?.isActionRequiredByLsp}
                    isInvalid={!!error}
                    validationMessage={error?.message}
                  />
                )}
              />
            </div>
          </div>

          {/* Comments Card */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-6'>
            <h3 className='text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 flex items-center gap-2'>
              <svg
                className='w-4 h-4 text-gray-400'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z'
                />
              </svg>
              Comments
            </h3>

            {/* Previous Comments */}
            <CommentThread
              comments={match?.comments ?? []}
              currentBusinessName={user?.businessName ?? ''}
            />

            <Controller
              name='comment'
              control={control}
              render={({ field }) => (
                <Textarea
                  label='Add a comment'
                  name='comment'
                  placeholder='Add any comments or notes for the warehouse...'
                  value={field.value}
                  onChange={field.onChange}
                  disabled={match?.isActionRequiredByLsp}
                />
              )}
            />
          </div>

          <p className='mt-6 text-xs text-gray-400 text-center'>
            * Surcharges may apply for rush orders, peak season, delays,
            cancellations, late payments, and extraordinary service requests.
          </p>
        </div>

        {/* Sidebar - Disclaimer (Right Side) */}
        <div className='hidden lg:block w-80 flex-shrink-0'>
          <div className='sticky top-6 p-5 bg-gray-50 rounded-xl border border-gray-200'>
            <div className='flex items-start gap-3'>
              <svg
                className='w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                />
              </svg>
              <div>
                <p className='text-sm font-medium text-gray-700 mb-3'>
                  Pricing Information
                </p>
                <p className='text-xs text-gray-500 mb-3'>
                  All prices are exclusive of VAT and FLOX transaction fees.
                </p>
                {match?.floxTransactionFees && (
                  <div className='space-y-2 text-xs text-gray-500'>
                    <div className='flex justify-between py-1.5 border-b border-gray-200'>
                      <span>Storage</span>
                      <span className='font-medium text-gray-700'>
                        {match.floxTransactionFees.storageFeePercentage}%
                      </span>
                    </div>
                    <div className='flex justify-between py-1.5 border-b border-gray-200'>
                      <span>Handling</span>
                      <span className='font-medium text-gray-700'>
                        {match.floxTransactionFees.handlingFeePercentage}%
                      </span>
                    </div>
                    <div className='flex justify-between py-1.5 border-b border-gray-200'>
                      <span>Other Services</span>
                      <span className='font-medium text-gray-700'>
                        {match.floxTransactionFees.otherServicesPercentage}%
                      </span>
                    </div>
                    <div className='flex justify-between py-1.5 border-b border-gray-200'>
                      <span>Haulage (FTL/LTL)</span>
                      <span className='font-medium text-gray-700'>
                        {match.floxTransactionFees.haulageFeePercentage}%
                      </span>
                    </div>
                    <div className='flex justify-between py-1.5'>
                      <span>Urban Distribution</span>
                      <span className='font-medium text-gray-700'>
                        {match.floxTransactionFees.distributionFeePercentage}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Disclaimer (shown below on smaller screens) */}
        <div className='lg:hidden mt-6 p-5 bg-gray-50 rounded-xl border border-gray-200'>
          <div className='flex items-start gap-3'>
            <svg
              className='w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
            <div>
              <p className='text-sm font-medium text-gray-700 mb-2'>
                All prices are exclusive of VAT and FLOX transaction fees
              </p>
              {match?.floxTransactionFees && (
                <div className='flex flex-wrap gap-2 text-xs text-gray-500'>
                  <span className='bg-white px-2 py-1 rounded border border-gray-200'>
                    Storage: {match.floxTransactionFees.storageFeePercentage}%
                  </span>
                  <span className='bg-white px-2 py-1 rounded border border-gray-200'>
                    Handling: {match.floxTransactionFees.handlingFeePercentage}%
                  </span>
                  <span className='bg-white px-2 py-1 rounded border border-gray-200'>
                    Other: {match.floxTransactionFees.otherServicesPercentage}%
                  </span>
                  <span className='bg-white px-2 py-1 rounded border border-gray-200'>
                    Haulage: {match.floxTransactionFees.haulageFeePercentage}%
                  </span>
                  <span className='bg-white px-2 py-1 rounded border border-gray-200'>
                    Distribution:{' '}
                    {match.floxTransactionFees.distributionFeePercentage}%
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </FormPage>
  );
}
